import React, { useState, useRef, useEffect } from 'react';
import { 
  ArrowLeft, 
  ArrowRight, 
  RotateCw, 
  X,
  Home,
  Search,
  Star,
  StarOff,
  Copy,
  Share2,
  Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';

export default function JuguangBrowser() {
  const [searchParams] = useSearchParams();
  const initialUrl = searchParams.get('url') || 'https://www.baidu.com';
  
  const [currentUrl, setCurrentUrl] = useState(initialUrl);
  const [urlInput, setUrlInput] = useState(initialUrl);
  const [isLoading, setIsLoading] = useState(false);
  const [bookmarks, setBookmarks] = useState<string[]>([]);
  const [history, setHistory] = useState<string[]>([initialUrl]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // 从 localStorage 读取书签
    const stored = localStorage.getItem('jg-bookmarks');
    if (stored) {
      try {
        setBookmarks(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse bookmarks:', e);
      }
    }
  }, []);

  // 处理导航
  const handleNavigate = (url?: string) => {
    const targetUrl = url || urlInput.trim();
    if (!targetUrl) return;

    let finalUrl = targetUrl;
    
    // 如果不是完整URL，添加协议
    if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
      // 判断是否是搜索关键词
      if (finalUrl.includes(' ') || !finalUrl.includes('.')) {
        // 使用百度搜索
        finalUrl = `https://www.baidu.com/s?wd=${encodeURIComponent(finalUrl)}`;
      } else {
        finalUrl = `https://${finalUrl}`;
      }
    }

    setIsLoading(true);
    setCurrentUrl(finalUrl);
    setUrlInput(finalUrl);
    
    // 添加到历史记录
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(finalUrl);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  // 后退
  const handleBack = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const url = history[newIndex];
      setHistoryIndex(newIndex);
      setCurrentUrl(url);
      setUrlInput(url);
      setIsLoading(true);
    }
  };

  // 前进
  const handleForward = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const url = history[newIndex];
      setHistoryIndex(newIndex);
      setCurrentUrl(url);
      setUrlInput(url);
      setIsLoading(true);
    }
  };

  // 刷新
  const handleRefresh = () => {
    if (iframeRef.current) {
      setIsLoading(true);
      iframeRef.current.src = currentUrl;
    }
  };

  // 收藏
  const handleBookmark = () => {
    const isBookmarked = bookmarks.includes(currentUrl);
    let newBookmarks: string[];

    if (isBookmarked) {
      newBookmarks = bookmarks.filter(b => b !== currentUrl);
      toast.success('已取消收藏');
    } else {
      newBookmarks = [...bookmarks, currentUrl];
      toast.success('已添加到收藏');
    }

    setBookmarks(newBookmarks);
    localStorage.setItem('jg-bookmarks', JSON.stringify(newBookmarks));
  };

  // 复制链接
  const handleCopyUrl = () => {
    navigator.clipboard.writeText(currentUrl);
    toast.success('链接已复制');
  };

  // 分享
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: '聚光浏览器',
        url: currentUrl,
      }).catch(() => {
        handleCopyUrl();
      });
    } else {
      handleCopyUrl();
    }
  };

  // 返回首页
  const handleGoHome = () => {
    navigate('/');
  };

  // iframe加载完成
  const handleIframeLoad = () => {
    setIsLoading(false);
    
    // 尝试劫持iframe内的链接和window.open（跨域限制可能失败）
    try {
      const iframeWindow = iframeRef.current?.contentWindow;
      const iframeDoc = iframeRef.current?.contentDocument;
      
      if (iframeDoc) {
        // 强制所有链接在iframe内打开
        const links = iframeDoc.querySelectorAll('a');
        links.forEach(link => {
          link.setAttribute('target', '_self');
        });
        
        // 劫持window.open
        if (iframeWindow) {
          iframeWindow.open = function(url?: string | URL) {
            if (url) {
              handleNavigate(url.toString());
            }
            return null;
          };
        }
      }
    } catch (e) {
      // 跨域限制，无法访问iframe内容
      console.log('Cannot access iframe content due to CORS');
    }
  };

  const isBookmarked = bookmarks.includes(currentUrl);
  const canGoBack = historyIndex > 0;
  const canGoForward = historyIndex < history.length - 1;

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* 顶部导航栏 */}
      <div className="shrink-0 bg-gradient-to-r from-primary/10 via-purple-500/10 to-primary/10 border-b shadow-sm">
        {/* Logo栏 */}
        <div className="px-4 py-3 border-b border-border/50 bg-background/50 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="bg-gradient-to-br from-primary to-purple-600 p-2 rounded-lg">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold gradient-text">聚光浏览器</h1>
                <p className="text-xs text-muted-foreground">Juguang Browser</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleGoHome}
              className="shrink-0"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* 控制栏 */}
        <div className="px-4 py-3">
          <div className="flex items-center gap-2">
            {/* 导航按钮 */}
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                disabled={!canGoBack}
                title="后退"
                className="h-9 w-9"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleForward}
                disabled={!canGoForward}
                title="前进"
                className="h-9 w-9"
              >
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleRefresh}
                title="刷新"
                className="h-9 w-9"
              >
                <RotateCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleGoHome}
                title="返回首页"
                className="h-9 w-9"
              >
                <Home className="h-4 w-4" />
              </Button>
            </div>

            {/* 地址栏 */}
            <div className="flex-1 flex items-center gap-2 bg-background rounded-lg px-3 py-2 border shadow-sm">
              <Search className="h-4 w-4 text-muted-foreground shrink-0" />
              <Input
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleNavigate()}
                placeholder="输入网址或搜索内容..."
                className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-sm"
              />
              <Button
                size="sm"
                onClick={() => handleNavigate()}
                className="shrink-0 h-7 px-3"
              >
                前往
              </Button>
            </div>

            {/* 功能按钮 */}
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBookmark}
                title={isBookmarked ? "取消收藏" : "添加收藏"}
                className="h-9 w-9"
              >
                {isBookmarked ? (
                  <Star className="h-4 w-4 fill-amber-500 text-amber-500" />
                ) : (
                  <StarOff className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCopyUrl}
                title="复制链接"
                className="h-9 w-9"
              >
                <Copy className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleShare}
                title="分享"
                className="h-9 w-9"
              >
                <Share2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* iframe内容区 */}
      <div className="flex-1 relative overflow-hidden">
        {isLoading && (
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-10">
            <div className="flex flex-col items-center gap-3">
              <div className="bg-gradient-to-br from-primary to-purple-600 p-3 rounded-full">
                <Sparkles className="h-6 w-6 text-white animate-pulse" />
              </div>
              <p className="text-sm text-muted-foreground">加载中...</p>
            </div>
          </div>
        )}
        <iframe
          ref={iframeRef}
          src={currentUrl}
          className="w-full h-full border-0"
          title="聚光浏览器"
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads allow-modals allow-top-navigation allow-popups-to-escape-sandbox allow-presentation"
          allow="accelerometer; autoplay; camera; clipboard-read; clipboard-write; encrypted-media; fullscreen; geolocation; gyroscope; microphone; midi; payment; picture-in-picture; usb; web-share"
          onLoad={handleIframeLoad}
        />
      </div>
    </div>
  );
}
